import React, {useCallback, useEffect, useRef, useState} from 'react';
import type {MutableRefObject} from 'react';

import omit from 'lodash/omit';
import CSSModules from 'react-css-modules';
import type Moveable from 'react-moveable';

import {EditorCanvas} from '../../../EditorCanvas/Canvas';
import {ObjectTypes} from '../../../EditorCanvas/types';
import type {BaseObject} from '../../../EditorCanvas/types';
import {useDirection} from '../../../hooks/useDirection';
import {useResizeObserver} from '../../../hooks/useResizeObserver';
import {focusRefChild} from '../../../utils/accessibilityUtils';
import {isObjectIn} from '../../../utils/lessonUtils';
import {renderMarkdown} from '../../../utils/markdownUtils';
import PreviewNavButton from '../PreviewNavButton';
import ImageContent from './ImageContent';

import styles from '../styles.module.scss';

type AdminEditorCanvasObject =
  | Extract<BaseObject, {type: 'text'}>
  | Extract<BaseObject, {type: 'emoji'}>;

type ImageSlideProps = {
  altText?: string;
  image: {
    url: string;
  };
  background: string;
  transition?: boolean;
  editMode?: boolean;
  reskinClassName?: string;
  showButtons: boolean;
  isButtonLightTheme?: boolean;
  isPreviewMode?: boolean;
  areDynamicSlidesEnabled?: boolean;
  overlayElements?: (
    | Extract<BaseObject, {type: 'text'}>
    | Extract<BaseObject, {type: 'emoji'}>
  )[];
  onCanvasObjectMove?: (object: AdminEditorCanvasObject) => void;
  onCanvasObjectReorder?: (object: AdminEditorCanvasObject) => void;
  removeText?: string;
  disableMoveableInteractions?: boolean;
  dynamicTextPlaceholder?: string;
  isMultiLanguage: boolean;
  setSelectedObject: (object: AdminEditorCanvasObject | null) => void;
  showDeleteTextWarning: boolean;
  setShowDeleteTextWarning: (show: boolean) => void;
  selectedObject: AdminEditorCanvasObject | null;
  removeObject: () => void;
  deleteTextWarning: React.ReactNode;
};

// eslint-disable-next-line complexity
const ImageSlide = ({
  image,
  altText = '',
  background,
  transition = false,
  editMode = false,
  reskinClassName = '',
  showButtons,
  isButtonLightTheme = false,
  isPreviewMode = false,
  areDynamicSlidesEnabled = false,
  overlayElements,
  onCanvasObjectMove,
  onCanvasObjectReorder,
  removeText,
  disableMoveableInteractions = false,
  dynamicTextPlaceholder,
  isMultiLanguage,
  setSelectedObject,
  showDeleteTextWarning,
  setShowDeleteTextWarning,
  selectedObject,
  removeObject,
  deleteTextWarning,
}: ImageSlideProps): JSX.Element => {
  const [selectedTarget, setSelectedTarget] =
    useState<MutableRefObject<HTMLElement> | null>(null);
  const moveableRef = useRef<Moveable>(null);
  const imageContentRef = useRef<HTMLDivElement>(null);
  const [imageDimensions, setImageDimensions] = useState({width: 0, height: 0});
  const [outerContainerDimensions, setOuterContainerDimensions] = useState({
    width: 0,
    height: 0,
  });
  const isLayoutReady =
    imageDimensions.width > 0 &&
    imageDimensions.height > 0 &&
    outerContainerDimensions.width > 0;

  let styleName = 'container';
  if (reskinClassName) {
    styleName += ' ' + reskinClassName;
  }
  if (transition) {
    styleName += ' fade';
  }
  if (editMode) {
    styleName += ' editing';
  }
  const outerContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (
        !(e.target as HTMLElement).closest('[data-testid="object-toolbar"]') &&
        !isObjectIn(e.target as HTMLElement, ['editor-object']) &&
        !showDeleteTextWarning
      ) {
        if (selectedObject) {
          setSelectedObject(null);
        }
      }
    };
    document.addEventListener('mousedown', onMouseDown);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [setSelectedObject, showDeleteTextWarning, selectedObject]);

  useEffect(() => {
    window.scrollTo(0, 0);
    focusRefChild(outerContainerRef);
  }, []);

  const handleImageResize = useCallback(
    ({width, height}: {width: number; height: number}) => {
      setImageDimensions((prev) =>
        prev.width !== width || prev.height !== height ? {width, height} : prev
      );
    },
    []
  );

  const handleOuterContainerResize = useCallback(
    ({width, height}: {width: number; height: number}) => {
      setOuterContainerDimensions({width, height});
    },
    []
  );

  useResizeObserver(imageContentRef, handleImageResize);
  useResizeObserver(outerContainerRef, handleOuterContainerResize);
  const direction = useDirection(outerContainerRef);

  const getCanvasContainerStyle = () => {
    if (
      disableMoveableInteractions &&
      !isPreviewMode &&
      isLayoutReady &&
      outerContainerRef.current
    ) {
      const isRTL = direction === 'rtl';
      const canvasContentOffset =
        (outerContainerDimensions.width - imageDimensions.width) / 2;

      return {
        transform: `translateX(${
          isRTL ? -canvasContentOffset : canvasContentOffset
        }px)`,
        inset: '16px 0 0',
        transformOrigin: 'top center',
        width: imageDimensions.width,
        height: imageDimensions.height,
        aspectRatio: 9 / 16,
      };
    }

    return {};
  };

  const getCanvasSize = () => {
    if (disableMoveableInteractions && !isPreviewMode && isLayoutReady) {
      return {
        width: imageDimensions.width,
        height: imageDimensions.height,
      };
    }

    return undefined;
  };

  const createMoveableObject = (o: BaseObject) => {
    switch (o.type) {
      case ObjectTypes.Text:
        return {
          ...o,
          htmlContent: renderMarkdown(o.text ?? ''),
        };

      case ObjectTypes.Emoji:
        return {
          ...o,
        };

      default:
        return null;
    }
  };

  const moveableObjects: AdminEditorCanvasObject[] = (overlayElements ?? [])
    .map(createMoveableObject)
    .filter((o): o is NonNullable<AdminEditorCanvasObject> => o !== null);

  const handleObjectMouseDown = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    object: AdminEditorCanvasObject,
    containerRef: MutableRefObject<HTMLDivElement>
  ) => {
    const nativeEvent = e.nativeEvent;

    Promise.resolve()
      .then(() => {
        setSelectedObject(object);
        setSelectedTarget(containerRef);
      })
      .then(() => moveableRef.current?.dragStart(nativeEvent));

    if (object?.type !== overlayElements?.[1]?.type) {
      onCanvasObjectReorder?.(object);
    }
  };

  const handleObjectLayoutChange = (
    event: 'start' | 'end',
    s: AdminEditorCanvasObject['styles']
  ) => {
    if (!selectedObject) {
      return;
    }

    if (selectedObject.type === 'text') {
      const object = {
        ...selectedObject,
        styles: {...selectedObject.styles, ...s},
      };

      if (event === 'start') {
        setSelectedObject(object);
      } else {
        onCanvasObjectMove?.(object);
      }
    } else {
      const objectStyles = omit(s, 'fontSize');
      const object = {
        ...selectedObject,
        styles: {...selectedObject.styles, ...objectStyles},
      };

      if (event === 'start') {
        setSelectedObject(object);
      } else {
        onCanvasObjectMove?.(object);
      }
    }
  };

  const handleRemoveObject = () => {
    if (isMultiLanguage && selectedObject?.type === 'text') {
      setShowDeleteTextWarning(true);
    } else {
      removeObject();
    }
  };

  return (
    <div
      styleName={styleName}
      style={{
        backgroundColor: background,
        ...(areDynamicSlidesEnabled && {
          overflowX: 'hidden',
        }),
      }}
      data-automation={`slide-preview${isPreviewMode ? '-thumbnail' : ''}`}
      ref={outerContainerRef}
    >
      {areDynamicSlidesEnabled && (
        <EditorCanvas<AdminEditorCanvasObject>
          containerStyle={getCanvasContainerStyle()}
          selectedTarget={selectedTarget}
          selectedObject={selectedObject}
          moveableRef={moveableRef}
          containerOpacity={1}
          moveableObjects={moveableObjects}
          setSelectedTarget={setSelectedTarget}
          setSelectedObject={setSelectedObject}
          handleObjectMouseDown={handleObjectMouseDown}
          handleObjectLayoutChange={handleObjectLayoutChange}
          toolbarProps={{
            handleRemoveObject,
            removeText,
            selectedObject,
          }}
          canvasSize={getCanvasSize()}
          disableMoveableInteractions={disableMoveableInteractions}
          isPreviewMode={isPreviewMode}
          objectPlacementEnabled={isLayoutReady}
          dynamicTextPlaceholder={dynamicTextPlaceholder}
        />
      )}

      {!!image && (
        <ImageContent
          image={image}
          altText={altText}
          editMode={editMode}
          isPreviewMode={isPreviewMode}
          containerRef={imageContentRef}
        />
      )}
      {showButtons && !isPreviewMode && (
        <PreviewNavButton
          button={{text: ''}}
          background={background}
          isLightTheme={isButtonLightTheme}
          showStickyBottomBackground={false}
        />
      )}
      {showDeleteTextWarning && deleteTextWarning}
    </div>
  );
};

export default CSSModules(ImageSlide, styles, {allowMultiple: true});
